import type { AppState, VoiceChatState } from "@/app/state/types";
import { getAppAccessToken } from "@/shared/data/appAuth";
import { readStoredAccessToken } from "@/shared/data/accessTokenStorage";
import {
	createDefaultMemoryConsoleTab,
	createDefaultMemoryInfoFilters,
	createDefaultMemoryPreferenceMode,
} from "@/shared/data/memoryTypes";
import { isAppMode } from "@/shared/utils/routing";
import { resolveDefaultVoiceAsrDefaults } from "@/features/voice/lib/voiceAsrProtocol";
import { resolveInitialThemeMode } from "@/shared/styles/theme";
import { readStoredTransportMode } from "@/features/transport/lib/transportMode";
import { restoreTerminalDockOpen } from "@/features/terminal/lib/terminalDockPersistence";

function createInitialVoiceChatState(): VoiceChatState {
	return {
		status: "idle",
		sessionActive: false,
		partialUserText: "",
		partialAssistantText: "",
		activeAssistantContentId: "",
		activeRequestId: "",
		activeTtsTaskId: "",
		ttsCommitted: false,
		error: "",
		wsStatus: "idle",
		capabilities: null,
		capabilitiesLoaded: false,
		capabilitiesError: "",
		voices: [],
		voicesLoaded: false,
		voicesError: "",
		selectedVoice: "",
		speechRate: 1.2,
		clientGate: resolveDefaultVoiceAsrDefaults().clientGate,
		clientGateCustomized: false,
		currentAgentKey: "",
		currentAgentName: "",
	};
}

export function createInitialState(): AppState {
	const appMode = isAppMode();
	const storedToken = appMode
		? getAppAccessToken() || ""
		: readStoredAccessToken();
	const themeMode = resolveInitialThemeMode();
	const transportMode = appMode ? "ws" : readStoredTransportMode() || "ws";

	return {
		agents: [],
		teams: [],
		chats: [],
		automations: [],
		sidebarPendingRequestCount: 0,
		chatAgentById: new Map(),
		runAgentById: new Map(),
		currentRunAgentKey: "",
		pendingNewChatAgentKey: "",
		workerPriorityKey: "",
		temporaryPinnedAgentKey: "",
		chatId: "",
		currentChatActiveRun: null,
		runId: "",
		requestId: "",
		streaming: false,
		abortController: null,
		messagesById: new Map(),
		messageOrder: [],
		events: [],
		debugEvents: [],
		debugLines: [],
		artifacts: [],
		fileChanges: [],
		plan: null,
		planRuntimeByTaskId: new Map(),
		taskItemsById: new Map(),
		activeTaskIds: new Set(),
		planCurrentRunningTaskId: "",
		planLastTouchedTaskId: "",
		toolStates: new Map(),
		toolNodeById: new Map(),
		contentNodeById: new Map(),
		pendingTools: new Map(),
		reasoningNodeById: new Map(),
		reasoningCollapseTimers: new Map(),
		actionStates: new Map(),
		executedActionIds: new Set(),
		timelineNodes: new Map(),
		timelineOrder: [],
		timelineNodeByMessageId: new Map(),
		timelineDomCache: new Map(),
		timelineCounter: 0,
		renderQueue: {
			dirtyNodeIds: new Set(),
			scheduled: false,
			stickToBottomRequested: false,
			fullSyncNeeded: false,
		},
		activeReasoningKey: "",
		chatFilter: "",
		conversationMode: "worker",
		workerSelectionKey: "",
		workerRows: [],
		workerIndexByKey: new Map(),
		workerRelatedChats: [],
		workerChatPanelCollapsed: true,
		chatLoadSeq: 0,
		memoryConsoleTab: createDefaultMemoryConsoleTab(),
		memoryInfoLoading: false,
		memoryInfoError: "",
		memoryInfoRecords: [],
		memoryInfoSelectedRecordId: "",
		memoryInfoDetail: null,
		memoryInfoDetailLoading: false,
		memoryInfoDetailError: "",
		memoryInfoFilters: createDefaultMemoryInfoFilters(),
		memoryInfoNextCursor: "",
		memoryMeta: null,
		memoryPreferenceScopes: [],
		memoryPreferenceActiveScopeType: "agent",
		memoryPreferenceActiveScopeKey: "",
		memoryPreferenceLabel: "AGENT",
		memoryPreferenceFileName: "AGENT.md",
		memoryPreferenceMeta: null,
		memoryPreferenceLoading: false,
		memoryPreferenceError: "",
		memoryPreferenceMode: createDefaultMemoryPreferenceMode(),
		memoryPreferenceMarkdownDraft: "",
		memoryPreferenceRecordsDraft: [],
		memoryPreferenceSelectedRecordId: "",
		memoryPreferenceDirty: false,
		memoryPreferenceSaving: false,
		memoryPreferenceSaveSummary: null,
		memoryPreferenceValidation: null,
		memoryPreviewDraft: "",
		memoryPreviewLoading: false,
		memoryPreviewError: "",
		memoryPreviewResult: null,
		memoryPreviewPromptLayer: "stable",
		leftDrawerOpen: true,
		rightSidebarOpen: false,
		rightSidebarOpenTab: null,
		activeSourceDetail: null,
		planningPreviews: [],
		terminalDockOpen: restoreTerminalDockOpen(),
		attachmentPreview: [],
		artifactExpanded: false,
		artifactManualOverride: null,
		artifactAutoCollapseTimer: null,
		planExpanded: false,
		planManualOverride: null,
		planAutoCollapseTimer: null,
		mentionOpen: false,
		mentionSuggestions: [],
		mentionActiveIndex: 0,
		activeFrontendTool: null,
		activeAwaiting: null,
		pendingAwaitings: [],
		themeMode,
		transportMode,
		wsStatus: "disconnected",
		wsErrorMessage: "",
		accessToken: storedToken,
		audioMuted: false,
		ttsDebugStatus: "idle",
		planningMode: false,
		planningModeByChatId: {},
		usageSnapshot: null,
		usagePopoverOpen: false,
		inputMode: "text",
		voiceChat: createInitialVoiceChatState(),
		composerDraft: "",
		composerDraftByChatId: {},
		pendingSteers: {},
		downvotedRunKeys: new Set(),
		eventPopoverIndex: -1,
		eventPopoverEventRef: null,
		eventPopoverAnchor: null,
		commandStatusOverlay: {
			visible: false,
			commandType: null,
			phase: "success",
			text: "",
			timer: null,
		},
	};
}
